/**
 * Pure text transforms behind the markdown editor toolbar.
 *
 * Every command takes the full text plus the current selection and returns the new text with the
 * selection to restore — no DOM, no React, so the fiddly parts (toggling off, multi-line prefixes,
 * caret placement) are unit-testable.
 *
 * @typedef {{text: string, start: number, end: number}} EditResult
 */

/**
 * Wrap/unwrap an inline marker (`**`, `*`, `~~`, `` ` ``) around the selection.
 *
 * Toggling off handles both shapes users produce: markers inside the selection (they selected the
 * bold text including its asterisks) and markers just outside it (they selected only the words).
 */
export function toggleInlineWrap(text, start, end, marker, placeholder = '') {
  const before = text.slice(0, start);
  const selected = text.slice(start, end);
  const after = text.slice(end);
  const width = marker.length;

  if (selected.length >= width * 2 && selected.startsWith(marker) && selected.endsWith(marker)) {
    const inner = selected.slice(width, -width);
    return { text: before + inner + after, start, end: start + inner.length };
  }

  if (before.endsWith(marker) && after.startsWith(marker)) {
    return {
      text: before.slice(0, -width) + selected + after.slice(width),
      start: start - width,
      end: end - width,
    };
  }

  if (start === end) {
    // No selection: drop in a placeholder and select it, so typing replaces it.
    return {
      text: before + marker + placeholder + marker + after,
      start: start + width,
      end: start + width + placeholder.length,
    };
  }

  return { text: before + marker + selected + marker + after, start: start + width, end: end + width };
}

/** Expand a selection to cover the whole lines it touches. */
function lineBounds(text, start, end) {
  const lineStart = text.lastIndexOf('\n', start - 1) + 1;
  let lineEnd = text.indexOf('\n', end);
  if (lineEnd === -1) lineEnd = text.length;
  return { lineStart, lineEnd };
}

/**
 * Toggle a line prefix (`## `, `- `, `> `) across every line the selection touches.
 *
 * `siblings` are prefixes this one replaces rather than stacks with — so turning an H2 into an H1
 * swaps the marker instead of producing `# ## text`, and a bullet becomes a quote cleanly.
 */
export function toggleLinePrefix(text, start, end, prefix, siblings = []) {
  const { lineStart, lineEnd } = lineBounds(text, start, end);
  const block = text.slice(lineStart, lineEnd);
  const lines = block.split('\n');
  const allPrefixed = lines.every((line) => line.startsWith(prefix));

  const next = lines
    .map((line) => {
      if (allPrefixed) return line.slice(prefix.length);
      let bare = line;
      for (const sibling of [prefix, ...siblings]) {
        if (bare.startsWith(sibling)) {
          bare = bare.slice(sibling.length);
          break;
        }
      }
      return prefix + bare;
    })
    .join('\n');

  const delta = next.length - block.length;
  const firstLineDelta = allPrefixed ? -prefix.length : prefix.length;
  return {
    text: text.slice(0, lineStart) + next + text.slice(lineEnd),
    // Keep the caret on the same character it was on, not at the line start.
    start: Math.max(lineStart, start + firstLineDelta),
    end: Math.max(lineStart, end + delta),
  };
}

/** Renumber an ordered list so `1.` lines come out sequential. */
export function toggleOrderedList(text, start, end) {
  const { lineStart, lineEnd } = lineBounds(text, start, end);
  const lines = text.slice(lineStart, lineEnd).split('\n');
  const numbered = /^\d+\.\s/;
  const allNumbered = lines.every((line) => numbered.test(line));

  const next = lines
    .map((line, index) => (allNumbered ? line.replace(numbered, '') : `${index + 1}. ${line.replace(/^-\s/, '')}`))
    .join('\n');

  return {
    text: text.slice(0, lineStart) + next + text.slice(lineEnd),
    start: lineStart,
    end: lineStart + next.length,
  };
}

/**
 * Insert a link. With a selection it becomes the label and the caret lands in the URL slot;
 * without one, the label placeholder is selected so typing replaces it.
 */
export function insertLink(text, start, end) {
  const before = text.slice(0, start);
  const selected = text.slice(start, end);
  const after = text.slice(end);

  if (selected) {
    const composed = `[${selected}](url)`;
    const urlStart = start + selected.length + 3;
    return { text: before + composed + after, start: urlStart, end: urlStart + 3 };
  }
  return { text: `${before}[text](url)${after}`, start: start + 1, end: start + 5 };
}
